import React, { useEffect, useState } from "react";
import api, { BACKEND_URL, showAlert } from "../../util/api";

const API_URL = "/admin/profile";

const Profile = () => {

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState("");
    const [imagePreview, setImagePreview] = useState(null);

    const [form, setForm] = useState({
        title: "",
        first_name: "",
        middle_name: "",
        last_name: "",
        full_name: "",
        initials: "",
        motto: "",
        image: null,

        email: "",
        phone: "",

        facebook: "",
        instagram: "",
        youtube: "",
        x: "",
    });


    /*
    |--------------------------------------------------------------------------
    | Load profile
    |--------------------------------------------------------------------------
    */

    useEffect(() => {
        fetchProfile();
    }, []);


    const fetchProfile = async () => {

        try {

            const response = await api.get(API_URL);

            const result = await response.data;

            if (result.success) {

                setForm({
                    title: result.data.title || "",
                    first_name: result.data.first_name || "",
                    middle_name: result.data.middle_name || "",
                    last_name: result.data.last_name || "",
                    full_name: result.data.full_name || "",
                    initials: result.data.initials || "",
                    motto: result.data.motto || "",
                    image: null,

                    email: result.data.email || "",
                    phone: result.data.phone || "",

                    facebook: result.data.facebook || "",
                    instagram: result.data.instagram || "",
                    youtube: result.data.youtube || "",
                    x: result.data.x || "",
                });

                if (result.data.image) {
                    setImagePreview(
                        `${BACKEND_URL}/storage/${result.data.image}`
                    );
                }
            }

        } catch (error) {

            console.error(
                "Failed to load profile",
                error
            );

        } finally {

            setLoading(false);
        }
    };


    /*
    |--------------------------------------------------------------------------
    | Handle input
    |--------------------------------------------------------------------------
    */

    const handleChange = (e) => {

        const { name, value } = e.target;

        setForm(prev => ({
            ...prev,
            [name]: value,
        }));
    };


    /*
    |--------------------------------------------------------------------------
    | Handle image
    |--------------------------------------------------------------------------
    */

    const handleImage = (e) => {

        const file = e.target.files?.[0];

        if (!file) return;

        setForm(prev => ({
            ...prev,
            image: file,
        }));

        setImagePreview(
            URL.createObjectURL(file)
        );
    };


    /*
    |--------------------------------------------------------------------------
    | Save profile
    |--------------------------------------------------------------------------
    */

    const handleSubmit = async (e) => {

        e.preventDefault();

        setSaving(true);
        setMessage("");


        const data = new FormData();

        Object.entries(form).forEach(
            ([key, value]) => {

                if (
                    value !== null &&
                    value !== undefined
                ) {
                    data.append(key, value);
                }
            }
        );


        try {

            const response = await api.post(API_URL, data, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });


            const result =
                await response.data;


            if (result.success  === false) {
                throw new Error(
                    result.message ||
                    "Unable to save profile."
                );
            }

            showAlert('success', 'success!', "Profile updated successfully.");


            setMessage(
                "Profile updated successfully."
            );


            if (result.data?.image) {

                setImagePreview(
                    `${BACKEND_URL}/storage/${result.data.image}`
                );

                setForm(prev => ({
                    ...prev,
                    image: null,
                }));
            }

        } catch (error) {

            setMessage(
                error.message ||
                "Something went wrong."
            );

        } finally {

            setSaving(false);
        }
    };


    if (loading) {

        return (
            <div className="admin-page-loading">
                Loading profile...
            </div>
        );
    }


    return (
        <div className="admin-profile-page">

            {/* HEADER */}

            <div className="admin-page-header">

                <div>

                    <span className="admin-page-eyebrow">
                        WEBSITE / PROFILE
                    </span>

                    <h2>
                        Politician <em>Profile.</em>
                    </h2>

                    <p>
                        Manage the official identity,
                        contact information and profile
                        image.
                    </p>

                </div>

                <button
                    type="submit"
                    form="profile-form"
                    className="admin-primary-button"
                    disabled={saving}
                >
                    {saving
                        ? "Saving..."
                        : "Save Changes"
                    }
                </button>

            </div>


            {message && (

                <div className="admin-form-message">
                    {message}
                </div>

            )}


            <form
                id="profile-form"
                onSubmit={handleSubmit}
            >

                <div className="admin-profile-grid">


                    {/* ======================================
                        IMAGE
                    ====================================== */}

                    <section className="admin-panel">

                        <div className="admin-panel-header">

                            <div>

                                <span>
                                    PORTRAIT
                                </span>

                                <h3>
                                    Profile Image
                                </h3>

                            </div>

                        </div>


                        <div className="admin-profile-image-area">

                            <div className="admin-profile-image">

                                {imagePreview ? (

                                    <img
                                        src={imagePreview}
                                        alt="Profile"
                                    />

                                ) : (

                                    <span>
                                        {form.initials || "AF"}
                                    </span>

                                )}

                            </div>


                            <label className="admin-upload-button">

                                Choose Photo

                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={
                                        handleImage
                                    }
                                />

                            </label>


                            <p>
                                JPG, PNG or WEBP.
                                Maximum 5MB.
                            </p>

                        </div>

                    </section>


                    {/* ======================================
                        PERSONAL INFORMATION
                    ====================================== */}

                    <section className="admin-panel">

                        <div className="admin-panel-header">

                            <div>

                                <span>
                                    IDENTITY
                                </span>

                                <h3>
                                    Personal Information
                                </h3>

                            </div>

                        </div>


                        <div className="admin-form">

                            <div className="admin-form-row">

                                <div className="admin-form-group small">

                                    <label>
                                        Title
                                    </label>

                                    <input
                                        name="title"
                                        value={
                                            form.title
                                        }
                                        onChange={
                                            handleChange
                                        }
                                    />

                                </div>


                                <div className="admin-form-group">

                                    <label>
                                        First Name
                                    </label>

                                    <input
                                        name="first_name"
                                        value={
                                            form.first_name
                                        }
                                        onChange={
                                            handleChange
                                        }
                                    />

                                </div>

                            </div>


                            <div className="admin-form-row">

                                <div className="admin-form-group">

                                    <label>
                                        Middle Name
                                    </label>

                                    <input
                                        name="middle_name"
                                        value={
                                            form.middle_name
                                        }
                                        onChange={
                                            handleChange
                                        }
                                    />

                                </div>


                                <div className="admin-form-group">

                                    <label>
                                        Last Name
                                    </label>

                                    <input
                                        name="last_name"
                                        value={
                                            form.last_name
                                        }
                                        onChange={
                                            handleChange
                                        }
                                    />

                                </div>

                            </div>


                            <div className="admin-form-group">

                                <label>
                                    Full Name
                                </label>

                                <input
                                    name="full_name"
                                    value={
                                        form.full_name
                                    }
                                    onChange={
                                        handleChange
                                    }
                                />

                            </div>


                            <div className="admin-form-row">

                                <div className="admin-form-group">

                                    <label>
                                        Initials
                                    </label>

                                    <input
                                        name="initials"
                                        value={
                                            form.initials
                                        }
                                        onChange={
                                            handleChange
                                        }
                                        maxLength={10}
                                    />

                                </div>


                                <div className="admin-form-group">

                                    <label>
                                        Motto
                                    </label>

                                    <input
                                        name="motto"
                                        value={
                                            form.motto
                                        }
                                        onChange={
                                            handleChange
                                        }
                                    />

                                </div>

                            </div>

                        </div>

                    </section>

                </div>


                {/* ==========================================
                    CONTACT
                ========================================== */}

                <section className="admin-panel admin-profile-contact">

                    <div className="admin-panel-header">

                        <div>

                            <span>
                                COMMUNICATION
                            </span>

                            <h3>
                                Contact & Social Media
                            </h3>

                        </div>

                    </div>


                    <div className="admin-form">

                        <div className="admin-form-row">

                            <div className="admin-form-group">

                                <label>
                                    Official Email
                                </label>

                                <input
                                    type="email"
                                    name="email"
                                    value={
                                        form.email
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    placeholder="office@example.com"
                                />

                            </div>


                            <div className="admin-form-group">

                                <label>
                                    Phone Number
                                </label>

                                <input
                                    name="phone"
                                    value={
                                        form.phone
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    placeholder="+234..."
                                />

                            </div>

                        </div>


                        <div className="admin-form-row">

                            <div className="admin-form-group">

                                <label>
                                    Facebook
                                </label>

                                <input
                                    name="facebook"
                                    value={
                                        form.facebook
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    placeholder="Facebook URL"
                                />

                            </div>


                            <div className="admin-form-group">

                                <label>
                                    Instagram
                                </label>

                                <input
                                    name="instagram"
                                    value={
                                        form.instagram
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    placeholder="Instagram URL"
                                />

                            </div>

                        </div>


                        <div className="admin-form-row">

                            <div className="admin-form-group">

                                <label>
                                    YouTube
                                </label>

                                <input
                                    name="youtube"
                                    value={
                                        form.youtube
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    placeholder="YouTube URL"
                                />

                            </div>


                            <div className="admin-form-group">

                                <label>
                                    X
                                </label>

                                <input
                                    name="x"
                                    value={
                                        form.x
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    placeholder="X profile URL"
                                />

                            </div>

                        </div>

                    </div>

                </section>

            </form>

        </div>
    );
};

export default Profile;